import type { MetricsBrief } from "../api";

/**
 * What the calling campaigns did, beside what the conversations said.
 *
 * This is the half of the report nothing else in the business has. The
 * dashboard knows connect rate fell on Wednesday; Slack knows the customer
 * raised it on Thursday. Only a page holding both can say they are the same
 * story, and that is the whole reason the metrics agent feeds the report
 * rather than living on a tab of its own.
 *
 * Every finding shows the numbers it rests on. They were computed by a
 * deterministic detector, not by the model that wrote the prose — the model
 * chose what mattered and said why, and the figures underneath are how a
 * reader disagrees with it.
 */
export default function Platform({ brief }: { brief: MetricsBrief }) {
  return (
    <>
      <h2>
        Platform metrics
        <span className={`badge traj ${brief.trajectory}`}>{cap(brief.trajectory)}</span>
      </h2>

      <div className="summary">{brief.summary}</div>

      {brief.findings.length > 0 && (
        <div className="pfindings">
          {brief.findings.map((f, i) => (
            <div key={i} className={`finding ${f.severity}`}>
              <div className="titlerow">
                <b>{f.title}</b>
                <span className={`sev ${f.severity}`}>{cap(f.severity)}</span>
                {f.platform_fault && <span className="chip ours">Ours to fix</span>}
              </div>
              <p>{f.detail}</p>
              {f.evidence.length > 0 && (
                <div className="pev">
                  {f.evidence.map((e, j) => (
                    <span key={j} className="ev">
                      <span className="cid">{e.campaign}</span> {e.metric}
                      {e.on && ` on ${e.on}`}: <b>{fmt(e.value)}</b>
                      {e.baseline !== null && e.baseline !== undefined && (
                        <> vs {fmt(e.baseline)}</>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {brief.open_questions.length > 0 && (
        <ul className="oq">
          {brief.open_questions.map((q, i) => (
            <li key={i}>{q}</li>
          ))}
        </ul>
      )}
    </>
  );
}

/** Counts whole, rates and ladders to two places. */
const fmt = (v: number): string =>
  Math.abs(v) >= 100 || Number.isInteger(v) ? Math.round(v).toLocaleString() : v.toFixed(2);

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
